import { tz } from "@date-fns/tz";
import {
  dateRangePickerChipMonth,
  dateRangePickerChipNextMonth,
  dateRangePickerChipNextThirtyDays,
  dateRangePickerChipNextWeek,
  dateRangePickerChipToday,
  dateRangePickerChipYear,
} from "@inputs/date/DateRangePicker";
import { DateRangePickerTextFieldForm } from "@inputs/hook-form/DateRangePickerForm";
import InfoIcon from "@mui/icons-material/Info";
import { DialogContent, Stack, Typography } from "@mui/material";
import { useI18nContext } from "@root/utils/organisation";
import { DateInputForm, StackGrid, TextFieldForm } from "@styleguide";
import { startOfDay } from "date-fns";
import { type Control, type UseFormSetValue, useWatch } from "react-hook-form";
import type { IModalActionOptions } from "../index";
import type { IModalActionForm, IModalActionResolver, TModalAction } from "../logics/types";
import { ModalActionFormActionType } from "./actionType";
import { ModalActionFormAddress } from "./address";
import { ModalActionFormApproach } from "./approach";
import { BeneficiaryComment } from "./beneficiaryComment";
import { ModalActionFormCandidate } from "./candidate";
import { ModalActionFormChallenges } from "./challenges";
import { ModalActionFormComments } from "./comments";
import { ModalActionFormContactChannel } from "./contactChannel";
import { ModalActionFormConvocation } from "./convocation";
import { ModalActionFormJobFollowUp } from "./jobFollowUp";
import { ModalActionFormMissedReason } from "./missedReason";
import { ActionOptions } from "./options";
import { ModalActionFormOwner } from "./owner";
import { ModalActionFormSlotConflictAlert } from "./slotConflict";
import { ModalActionFormStatus } from "./status";
import { ModalActionFormTime } from "./time";
import { ModalActionFormTimeSpent } from "./timeSpent";

export interface IModalActionFormProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
  setValue: UseFormSetValue<IModalActionForm>;
  action?: TModalAction;
  options?: IModalActionOptions;
}

export const ModalActionForm = ({ control, setValue, action, options }: IModalActionFormProps) => {
  const { timeZone } = useI18nContext();
  const actionType = useWatch({ control, name: "type" });
  return (
    <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2SemiBold" color="primary">
        Contenu
      </Typography>
      <TextFieldForm control={control} name="title" label="Titre" size="small" />
      <StackGrid perLine={{ tablet: 2 }} columnGap={2} rowGap={2} strict>
        <ModalActionFormActionType
          control={control}
          disabledActionTypes={options?.disabledActionTypes}
          disableActionTypeField={options?.disableActionTypeField || action?.createdFromCer}
        />
        {!actionType?.metadata?.approachActionType && (
          <ModalActionFormChallenges control={control} disabled={actionType?.metadata?.approachActionType} />
        )}
      </StackGrid>
      <ModalActionFormJobFollowUp control={control} action={action} />
      {actionType?.metadata?.approachActionType && !options?.hideApproachField && (
        <StackGrid perLine={{ tablet: 2 }} columnGap={2} rowGap={2} strict>
          <ModalActionFormApproach control={control} setFormValue={setValue} />
          {actionType?.metadata?.approachActionType && (
            <ModalActionFormChallenges control={control} disabled={actionType?.metadata?.approachActionType} />
          )}
        </StackGrid>
      )}
      {(action?.communication?.email || action?.communication?.sms) && (
        <Stack sx={{ flexDirection: "row", alignItems: "start", gap: 1 }}>
          <InfoIcon fontSize="small" color="primary" />
          <Typography variant="caption" color="primary">
            Le contenu de la communication envoyée est visible sur l’action en mode consultation uniquement. Vous pouvez
            cependant la compléter ici avec les éventuelles réponses reçues.
          </Typography>
        </Stack>
      )}
      <ModalActionFormComments control={control} />
      <Stack>
        <BeneficiaryComment control={control} hidden={options?.hideBeneficiaryComment} setValue={setValue} />
      </Stack>
      <Typography variant="subtitle2SemiBold" color="primary">
        Modalités
      </Typography>
      <StackGrid perLine={{ tablet: 2 }} columnGap={2} rowGap={2} strict>
        <ModalActionFormCandidate
          control={control}
          action={action}
          setValue={setValue}
          hidden={options?.hideCandidateField}
          options={options}
        />
        <ModalActionFormOwner control={control} hidden={options?.hideOwnerField} />
        <ModalActionFormContactChannel control={control} setValue={setValue} />
        {!actionType?.metadata?.approachActionType && (
          <DateInputForm
            control={control}
            name="date"
            label="Jour *"
            slotProps={{
              clearButton: { sx: { mr: 1.5, p: 0.5, opacity: "1 !important" } },
              field: { clearable: true },
            }}
            TextFieldProps={{ size: "small" }}
            onChange={(value) =>
              value && setValue("status", value > startOfDay(new Date(), { in: tz(timeZone) }) ? "todo" : "done")
            }
          />
        )}
        {actionType?.metadata?.approachActionType && (
          <DateRangePickerTextFieldForm
            control={control}
            label="Période ou jour *"
            name="executionPeriod"
            onChange={(value) =>
              setValue(
                "status",
                value.endDate && value.endDate > startOfDay(new Date(), { in: tz(timeZone) }) ? "todo" : "done"
              )
            }
            required={{ startDate: true, endDate: true }}
            chips={[
              dateRangePickerChipToday,
              dateRangePickerChipMonth,
              dateRangePickerChipNextWeek,
              dateRangePickerChipNextThirtyDays,
              dateRangePickerChipNextMonth,
              dateRangePickerChipYear,
            ]}
          />
        )}
        <Stack>
          <ModalActionFormTime control={control} />
        </Stack>
      </StackGrid>
      <ModalActionFormSlotConflictAlert control={control} action={action} />
      <ModalActionFormConvocation control={control} hidden={options?.hideConvocationText} />
      <ModalActionFormAddress control={control} />
      <Typography variant="subtitle2SemiBold" color="primary">
        Statut
      </Typography>
      <ModalActionFormStatus control={control} />
      <StackGrid perLine={{ tablet: 2 }} columnGap={2} rowGap={2} strict>
        <ModalActionFormMissedReason control={control} />
        <ModalActionFormTimeSpent control={control} />
      </StackGrid>
      <ActionOptions control={control} />
    </DialogContent>
  );
};
